import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { lazyInject } from '../../ioc';
import { RouteParams } from '../estimate-routes';
import { EstimateStore, PriceOptions, PresentationSettingsState } from '../../stores/estimate.store';
import { Estimate, FinancingProvider } from 'src/api';

import { EstimatesPage } from './desktop/estimates-page';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { initializeLaunchDarkly } from 'src/util/launch-darkly-initializer';
import * as LDClient from 'launchdarkly-js-client-sdk';

@observer
export class EstimatesPageContainer extends React.Component<RouteComponentProps<RouteParams>> {
    @lazyInject(EstimateStore) estimateStore!: EstimateStore;
    @lazyInject(ClientDataStore) clientDataStore!: ClientDataStore;

    @observable estimates: Estimate[] | undefined;
    @observable priceOptions: PriceOptions | undefined;
    @observable selectedEstimate: Estimate | undefined;
    @observable errorMessage: string | undefined;
    @observable gotoTerms = this.estimateStore.gotoTerms;
    @observable financingEnabled: boolean | undefined;
    @observable financingPresentationSetting: PresentationSettingsState | undefined;
    @observable ldClient : LDClient.LDClient | undefined;
    @observable financingProvider: string | undefined;

    // for unit testing purposes
    getEstimatePromise: Promise<any> | undefined;

    constructor(props: RouteComponentProps<RouteParams>) {
        super(props);
        this.getEstimatePromise = this.getEstimate();
    }

    componentDidUpdate(prevProps: Readonly<RouteComponentProps<RouteParams>>) {
        const prevEstimateId = prevProps.match.params.estimateId;
        const estimateId = this.props.match.params.estimateId;
        if (prevEstimateId !== estimateId) {
            this.selectEstimate(estimateId);
        }
    }

    private selectEstimate(estimateId: string | undefined) {
        if (!this.estimates) {
            this.selectedEstimate = undefined;
            return;
        }
        this.selectedEstimate = this.estimates.find((e) => e.id === estimateId);

        if (!this.selectedEstimate && this.estimates.length > 0) {
            this.estimateStore.gotoEstimate(this.estimates[0].id, undefined, true);
        }
    }

    private async getEstimate() {
        const id = this.props.match.params.bundleId;
        if (!id) {
            return;
        }
        
        try {
            const bundle = await this.estimateStore.getEstimateBundle(id);
            this.estimates = bundle.estimates;
            this.priceOptions = bundle.priceOptions;
            if (this.estimates.length > 0) {
                const tenantId = this.estimates[0].tenantId;

                const estimateId = this.props.match.params.estimateId;
                if (!estimateId) {
                    this.estimateStore.gotoEstimate(
                        this.estimates[0].id,
                        this.props.match.params.bundleId,
                        true
                    );
                } else {
                    this.selectEstimate(estimateId);
                }

                const financingSettings = await this.estimateStore.getFinancingSettings(tenantId);

                const provider = financingSettings.allowedOnlineEstimatesProviders?.[0];
                if (provider) {
                    this.ldClient = await initializeLaunchDarkly(tenantId, provider);
                    this.financingProvider = provider;
                }                

                this.financingEnabled = !!(
                    financingSettings.allowedOnlineEstimatesProviders &&
                    financingSettings.allowedOnlineEstimatesProviders.length > 0 &&
                    financingSettings.allowedOnlineEstimatesProviders[0] === FinancingProvider.ServiceFinance &&
                    !this.ldClient?.variation('cp1-new-mfe', false)
                );
                this.financingPresentationSetting = 
                    this.estimateStore.mapPresentationType(financingSettings.presentationType);

                if (this.financingEnabled) {
                    this.estimates.forEach((estimate) => {
                        this.estimateStore.setCalculation(
                            false,
                            estimate.total || 0,
                            estimate.stateCode ?? '',
                            estimate.tenantId,
                            estimate.buId
                        );
                    });
                }
            }
        } catch (err) {
            this.errorMessage = String(err);
        }
    }

    clearError = () => {
        this.errorMessage = undefined;
    };

    gotoMobile = () => {
        this.props.history.push(`${this.props.match.url}/mobile`);
    };

    @computed
    get shouldShowApplyForFinancing(): boolean  {
        return !!this.financingEnabled &&
            this.estimateStore.calculations.size > 0;
    }

    render() {
        return <EstimatesPage container={this} />;
    }
}
